import { Packaging, PricePointAttributes } from 'api-client'
import * as R from 'ramda'

import { Product } from '~shared-components/model'
import {
  Currency,
  Price,
  pricePointAttributesToPriceTier,
  PriceTier,
} from '~shared-components/transforms'

export const formatPrice = (price: Price | undefined) =>
  price ? `${price.toFormat('USD0,0.00')}` : ``

export const sumPrices = (prices: (Price | undefined)[]) => {
  const filteredPrices = prices.filter(R.identity) as Price[]

  if (filteredPrices.length > 0) {
    const currency = filteredPrices[0].getCurrency() as Currency
    return filteredPrices.reduce(
      (sum, price) => sum.add(price),
      new Price({ amount: 0, currency }),
    )
  }
}

export const getSortedPrices = (
  pricePoints: PricePointAttributes[],
  ascending = true,
) =>
  [...pricePoints].sort((p1, p2) => {
    const p1ScaleStartQuantity = p1.scaleStart.quantity
    const p2ScaleStartQuantity = p2.scaleStart.quantity
    return ascending
      ? p1ScaleStartQuantity - p2ScaleStartQuantity
      : p2ScaleStartQuantity - p1ScaleStartQuantity
  })

export const getMatchingPricePoint = (
  pricePoints: PricePointAttributes[],
  orderAmount = 0,
) => {
  // Sort price points by cheapest bulk price,
  // so we can find the price matching the order amount
  const sortedPricePoints = getSortedPrices(pricePoints, false)

  return sortedPricePoints.find(p => orderAmount >= p.scaleStart.quantity)
}

export const getHighestTierPrice = (pricePoints: PricePointAttributes[]) =>
  R.head(getSortedPrices(pricePoints))

export const getLowestTierPrice = (pricePoints: PricePointAttributes[]) =>
  R.head(getSortedPrices(pricePoints, false))

export const canSumPrices = (prices: (Price | undefined)[]) =>
  Boolean(
    prices?.every(
      (pr, _, all) =>
        typeof pr !== 'undefined' && pr.getCurrency() === all[0]!.getCurrency(),
    ),
  )

export const getMinimumOrderAmount = (pricePoints: PricePointAttributes[]) =>
  getHighestTierPrice(pricePoints)?.scaleStart

export const getMinimumOrderQuantity = (
  pricePoints: PricePointAttributes[],
  packaging: Packaging,
): number => {
  const minimumOrderAmount = getMinimumOrderAmount(pricePoints)
  if (minimumOrderAmount && packaging.size.uom === minimumOrderAmount.uom) {
    const minimumOrderQuantity = Math.ceil(
      minimumOrderAmount.quantity / packaging.size.quantity,
    )
    return minimumOrderQuantity === 0 ? 1 : minimumOrderQuantity
  } else {
    return 1
  }
}

export const getPriceTiers = (
  product: Product,
): { price: PricePointAttributes; priceTier: PriceTier }[] =>
  product.pricePoints.map(price => ({
    price,
    priceTier: pricePointAttributesToPriceTier(price),
  }))

export const getFirstPriceTiers = (
  product: Product,
): { price: PricePointAttributes; priceTier: PriceTier } | undefined =>
  getPriceTiers(product)[0]

export const hasPriceTiers = (product: Product): boolean =>
  Boolean(getPriceTiers(product).length)
